import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';

import { Gate } from '../../../global/contexts/auth/auth';
import { useConfirm } from '../../../common/components/ConfirmDialog/confirm';
import { useAnimal, useUpdateAnimal, useDeleteAnimal } from '../contexts/animals';
import { validateAnimal } from '../utils/validate';
import { buildAnimalPayload } from '../utils/payload';
import AnimalFields from './fields';

import '../../../common/styles/catalog.css';

function buildForm(a) {
    return {
        baptized:    a.baptized !== false,
        name:        a.name || '',
        number:      a.number || '',
        register:    a.register || '',
        race_id:     a.race_id ? String(a.race_id) : '',
        sex:         a.sex || '',
        birth:       a.birth ? String(a.birth).slice(0, 10) : '',
        appearance:  a.appearance || '',
        description: a.description || '',
        status:      a.status ?? 2,
    };
}

export default function EditAnimalForm({ animalId }) {
    const navigate = useNavigate();
    const { data: animal, isLoading, error: loadError } = useAnimal(animalId);
    const { mutateAsync: updateAnimal, isPending: updating } = useUpdateAnimal(animalId);
    const { mutateAsync: deleteAnimal, isPending: deleting } = useDeleteAnimal();
    const confirm = useConfirm();

    const [form, setForm] = useState(null);
    const [seededFor, setSeededFor] = useState(null);
    const [localErrors, setLocalErrors] = useState({});
    const [serverFields, setServerFields] = useState({});

    if (animal && seededFor !== animal.id) {
        setSeededFor(animal.id);
        setForm(buildForm(animal));
    }

    const setField = (field, value) => {
        setForm(prev => ({ ...prev, [field]: value }));
        if (localErrors[field] || serverFields[field]) {
            setLocalErrors(prev => ({ ...prev, [field]: undefined }));
            setServerFields(prev => ({ ...prev, [field]: undefined }));
        }
    };
    const fieldError = (field) => localErrors[field] || serverFields[field];

    if (isLoading || (!form && !loadError)) return <div className="cat-form__state">Cargando animal…</div>;
    if (loadError || !animal) return <div className="cat-form__state">Animal no encontrado.</div>;

    const handleSubmit = async (e) => {
        e.preventDefault();
        const errors = validateAnimal(form);
        if (Object.keys(errors).length) { setLocalErrors(errors); toast.error('Revisá los campos marcados'); return; }
        setLocalErrors({});
        setServerFields({});

        try {
            await updateAnimal({ ...buildAnimalPayload(form), status: form.status });
            toast.success('Animal actualizado');
            navigate(`/admin/animals/${animalId}`);
        } catch (err) {
            if (err?.status === 409) { setServerFields({ register: 'Ya existe un animal con ese registro.' }); toast.error('Registro duplicado'); }
            else if (err?.fields) { setServerFields(err.fields); toast.error('Revisá los campos marcados'); }
            else toast.error(err?.message || 'No se pudo actualizar el animal');
        }
    };

    const handleDelete = async () => {
        const label = animal.name || animal.number || `#${animal.id}`;
        const ok = await confirm({
            title: 'Eliminar animal',
            message: `¿Eliminar el animal "${label}"? Pasará a estado eliminado.`,
            confirmLabel: 'Eliminar',
            danger: true,
        });
        if (!ok) return;
        try {
            await deleteAnimal(animalId);
            toast.success('Animal eliminado');
            navigate('/admin/animals');
        } catch (err) {
            if (err?.status === 409) toast.error('No se puede eliminar: tiene registros asociados');
            else toast.error(err?.message || 'No se pudo eliminar el animal');
        }
    };

    const baptized = form.baptized !== false;
    const canSubmit = !updating && (!baptized || (form.name.trim() && form.number.trim())) && !!form.race_id && !!form.sex;

    return (
        <form className="cat-form" onSubmit={handleSubmit}>
            <header className="cat-form__header">
                <Link to={`/admin/animals/${animalId}`} className="cat-form__back">Volver al animal</Link>
                <h1 className="cat-form__title">Editar animal</h1>
                <span className="cat-form__subtitle">#{animal.id}</span>
            </header>

            <AnimalFields form={form} setField={setField} fieldError={fieldError} disabled={updating} showStatus />

            <footer className="cat-form__footer">
                <Link to={`/admin/animals/${animalId}`} className="cat-form__cancel">Cancelar</Link>
                <Gate domain="animals" action="write">
                    <button type="button" className="cat-form__danger" onClick={handleDelete} disabled={updating || deleting}>
                        {deleting ? 'Eliminando…' : 'Eliminar'}
                    </button>
                </Gate>
                <button type="submit" className="cat-form__submit" disabled={!canSubmit}>
                    {updating ? 'Guardando…' : 'Guardar cambios'}
                </button>
            </footer>
        </form>
    );
}
